import { endOfDay, endOfMonth, format, isValid, parse, startOfDay, startOfMonth } from 'date-fns';
import { parseAsString, useQueryStates } from 'nuqs';
import { useCallback, useMemo } from 'react';

import { AppointmentTypeSelection, CalendarFiltersState, Option } from '@lichtblick/types';
import { getToday } from '@lichtblick/utils';

const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const parseOptionArray = <TValue,>(value: string | null): Option<TValue>[] | undefined => {
  if (!value) {
    return undefined;
  }

  try {
    return JSON.parse(value) as Option<TValue>[];
  } catch {
    return undefined;
  }
};

/**
 * Parse a URL date value that may be either a date-only string (yyyy-MM-dd) or a
 * legacy ISO 8601 string. Returns null when the value cannot be parsed.
 */
const parseDateParam = (value: string): Date | null => {
  if (DATE_ONLY_PATTERN.test(value)) {
    const d = parse(value, 'yyyy-MM-dd', new Date());

    return isValid(d) ? d : null;
  }

  const d = new Date(value);

  return isValid(d) ? d : null;
};

export const useCalendarFilters = () => {
  const [calendarFilterQuery, setCalendarFilterQuery] = useQueryStates({
    appointmentType: parseAsString,
    binding: parseAsString,
    branch: parseAsString,
    notAfterUtc: parseAsString,
    notBeforeUtc: parseAsString,
    partner: parseAsString,
    person: parseAsString,
    query: parseAsString,
    team: parseAsString,
  });

  const calendarFilters = useMemo<CalendarFiltersState>(() => {
    const currentDate = getToday();

    return {
      appointmentType: parseOptionArray<AppointmentTypeSelection>(calendarFilterQuery.appointmentType),
      binding: calendarFilterQuery.binding ?? 'all',
      branch: parseOptionArray(calendarFilterQuery.branch),
      notAfterUtc: calendarFilterQuery.notAfterUtc
        ? endOfDay(parseDateParam(calendarFilterQuery.notAfterUtc) ?? currentDate)
        : endOfMonth(currentDate),
      notBeforeUtc: calendarFilterQuery.notBeforeUtc
        ? startOfDay(parseDateParam(calendarFilterQuery.notBeforeUtc) ?? currentDate)
        : startOfMonth(currentDate),
      partner: parseOptionArray(calendarFilterQuery.partner),
      person: parseOptionArray(calendarFilterQuery.person),
      query: calendarFilterQuery.query ?? '',
      team: parseOptionArray(calendarFilterQuery.team),
    };
  }, [
    calendarFilterQuery.appointmentType,
    calendarFilterQuery.binding,
    calendarFilterQuery.branch,
    calendarFilterQuery.notAfterUtc,
    calendarFilterQuery.notBeforeUtc,
    calendarFilterQuery.partner,
    calendarFilterQuery.person,
    calendarFilterQuery.query,
    calendarFilterQuery.team,
  ]);

  const setQuery = useCallback(
    (query: string) => {
      void setCalendarFilterQuery({ query: query || null }, { history: 'replace' });
    },
    [setCalendarFilterQuery],
  );

  const setBranch = useCallback(
    (branch: Option[]) => {
      void setCalendarFilterQuery({ branch: branch.length ? JSON.stringify(branch) : null }, { history: 'replace' });
    },
    [setCalendarFilterQuery],
  );

  const setTeam = useCallback(
    (team: Option[]) => {
      void setCalendarFilterQuery({ team: team.length ? JSON.stringify(team) : null }, { history: 'replace' });
    },
    [setCalendarFilterQuery],
  );

  const setAppointmentType = useCallback(
    (appointmentType: Option<AppointmentTypeSelection>[]) => {
      void setCalendarFilterQuery(
        { appointmentType: appointmentType.length ? JSON.stringify(appointmentType) : null },
        { history: 'replace' },
      );
    },
    [setCalendarFilterQuery],
  );

  const setNotBefore = useCallback(
    (notBeforeUtc?: Date) => {
      void setCalendarFilterQuery(
        { notBeforeUtc: notBeforeUtc ? format(notBeforeUtc, 'yyyy-MM-dd') : null },
        { history: 'replace' },
      );
    },
    [setCalendarFilterQuery],
  );

  const setNotAfter = useCallback(
    (notAfterUtc?: Date) => {
      void setCalendarFilterQuery(
        { notAfterUtc: notAfterUtc ? format(notAfterUtc, 'yyyy-MM-dd') : null },
        { history: 'replace' },
      );
    },
    [setCalendarFilterQuery],
  );

  const setPerson = useCallback(
    (person: Option[]) => {
      void setCalendarFilterQuery({ person: person.length ? JSON.stringify(person) : null }, { history: 'replace' });
    },
    [setCalendarFilterQuery],
  );

  const setPartner = useCallback(
    (partner: Option[]) => {
      void setCalendarFilterQuery({ partner: partner.length ? JSON.stringify(partner) : null }, { history: 'replace' });
    },
    [setCalendarFilterQuery],
  );

  const setBinding = useCallback(
    (binding?: string) => {
      void setCalendarFilterQuery({ binding: binding && binding !== 'all' ? binding : null }, { history: 'replace' });
    },
    [setCalendarFilterQuery],
  );

  const clearAll = useCallback(() => {
    void setCalendarFilterQuery(
      {
        appointmentType: null,
        binding: null,
        branch: null,
        partner: null,
        person: null,
        team: null,
      },
      { history: 'replace' },
    );
  }, [setCalendarFilterQuery]);

  return {
    calendarFilters,
    clearAll,
    setAppointmentType,
    setBinding,
    setBranch,
    setNotAfter,
    setNotBefore,
    setPartner,
    setPerson,
    setQuery,
    setTeam,
  };
};
